const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';

function showScrollElement(element, cancelAnimation = false) {
  element.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);

  if (cancelAnimation) {
    element.classList.add(SCROLL_ANIMATION_CANCEL_CLASSNAME);
  }
}

function hideScrollElement(element) {
  element.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
  element.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
}

function elementIsVisibleOrAboveViewport(element) {
  const rect = element.getBoundingClientRect();

  return rect.top < window.innerHeight || rect.bottom < 0;
}

function forceVisibleElementsInsideViewport(rootEl = document) {
  const elements = Array.from(
    rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME)
  );

  elements.forEach((element) => {
    const rect = element.getBoundingClientRect();

    const isInsideViewport = rect.bottom > 0 && rect.top < window.innerHeight;

    if (isInsideViewport) {
      showScrollElement(element, true);
    }
  });
}

// Scroll in animation logic
function onIntersection(entries, observer) {
  entries.forEach((entry, index) => {
    const elementTarget = entry.target;
    const rect = entry.boundingClientRect;

    if (entry.isIntersecting) {
      showScrollElement(elementTarget);

      if (elementTarget.hasAttribute('data-cascade')) {
        elementTarget.style.setProperty('--animation-order', index);
      }

      observer.unobserve(elementTarget);
      return;
    }

    const isAboveViewport = rect.bottom < 0;
    const isAlreadyInViewport = rect.top < window.innerHeight;

    if (isAboveViewport || isAlreadyInViewport) {
      showScrollElement(elementTarget, true);
      observer.unobserve(elementTarget);
      return;
    }

    hideScrollElement(elementTarget);
  });
}

function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  const animationTriggerElements = Array.from(
    rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME)
  );

  if (animationTriggerElements.length === 0) return;

  if (isDesignModeEvent) {
    animationTriggerElements.forEach((element) => {
      element.classList.add('scroll-trigger--design-mode');
      showScrollElement(element, true);
    });
    return;
  }

  if (!('IntersectionObserver' in window)) {
    animationTriggerElements.forEach((element) => {
      showScrollElement(element, true);
    });
    return;
  }

  const observer = new IntersectionObserver(onIntersection, {
    rootMargin: '0px 0px -50px 0px',
    threshold: 0.01,
  });

  animationTriggerElements.forEach((element) => {
    if (elementIsVisibleOrAboveViewport(element)) {
      showScrollElement(element, true);
      return;
    }

    hideScrollElement(element);
    observer.observe(element);
  });

  setTimeout(() => {
    forceVisibleElementsInsideViewport(rootEl);
  }, 300);

  setTimeout(() => {
    forceVisibleElementsInsideViewport(rootEl);
  }, 1000);
}

// Zoom in animation logic
function initializeScrollZoomAnimationTrigger() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const animationTriggerElements = Array.from(
    document.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME)
  );

  if (animationTriggerElements.length === 0) return;

  const scaleAmount = 0.2 / 100;

  animationTriggerElements.forEach((element) => {
    let elementIsVisible = false;

    const observer = new IntersectionObserver((elements) => {
      elements.forEach((entry) => {
        elementIsVisible = entry.isIntersecting;
      });
    });

    observer.observe(element);

    element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));

    const scrollHandler = () => {
      if (!elementIsVisible) return;

      element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));
    };

    window.addEventListener('scroll', scrollHandler, { passive: true });
  });
}

function percentageSeen(element) {
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY;
  const elementPositionY = element.getBoundingClientRect().top + scrollY;
  const elementHeight = element.offsetHeight;

  if (elementPositionY > scrollY + viewportHeight) {
    return 0;
  }

  if (elementPositionY + elementHeight < scrollY) {
    return 100;
  }

  const distance = scrollY + viewportHeight - elementPositionY;
  const percentage = distance / ((viewportHeight + elementHeight) / 100);

  return Math.round(percentage);
}

window.addEventListener('DOMContentLoaded', () => {
  initializeScrollAnimationTrigger();
  initializeScrollZoomAnimationTrigger();
});

window.addEventListener('load', () => {
  forceVisibleElementsInsideViewport();
});

window.addEventListener(
  'scroll',
  () => {
    forceVisibleElementsInsideViewport();
  },
  { passive: true }
);

document.addEventListener('shopify:section:load', (event) => {
  initializeScrollAnimationTrigger(event.target, Shopify.designMode);
  forceVisibleElementsInsideViewport(event.target);
});

document.addEventListener('shopify:section:reorder', () => {
  initializeScrollAnimationTrigger(document, Shopify.designMode);
  forceVisibleElementsInsideViewport();
});